/**
 * What a registration attempt reports back.
 *
 * A registration outcome is returned and never thrown. Requirement 1.8 asks the
 * capability panel to state the cause of a refused registration, and the tool list
 * panel task 3.8 builds states the same thing per tool, so the outcome is written
 * to be rendered: a cause, the host's own error name, a readable message and the
 * tool name.
 *
 * Four causes, and only four, per requirement 11.7's neighbour in the design's
 * error handling section. `cause` is the classification a panel branches on;
 * `errorName` is what the host actually said, kept because the two are not always
 * the same and because the name is the fact requirement 1.8 names.
 */

import type { ThrownDetail } from './host';

export type RegistrationCause =
  | 'NotAllowedError'
  | 'DuplicateName'
  | 'InvalidDescriptor'
  | 'Unsupported';

export type RegistrationOutcome =
  | { readonly ok: true; readonly name: string }
  | {
      readonly ok: false;
      readonly name: string;
      readonly cause: RegistrationCause;
      /** The `name` of the thrown value, or `Unsupported` where nothing was thrown. */
      readonly errorName: string;
      /** One sentence a panel can print as it stands. */
      readonly message: string;
    };

/**
 * A name of a tool already registered, in whatever words the host chose.
 *
 * Names first, because a build that has a dedicated error for this is the
 * unambiguous case. The message patterns follow, because most builds throw a plain
 * `Error` or an `InvalidStateError` and put the fact in the sentence.
 */
const DUPLICATE_ERROR_NAMES: ReadonlyArray<string> = [
  'DuplicateNameError',
  'InvalidStateError',
  'InvalidModificationError',
];

const DUPLICATE_PHRASES = /already (registered|exists|in use|taken)|duplicate/i;

/** A descriptor the host would not accept: a budget, a schema, a missing field. */
const INVALID_ERROR_NAMES: ReadonlyArray<string> = [
  'TypeError',
  'SyntaxError',
  'DataError',
  'DataCloneError',
  'InvalidCharacterError',
];

const INVALID_PHRASES = /invalid|malformed|schema|descriptor|must be|too long|required/i;

/**
 * Classify a rejection into one of the four causes.
 *
 * The order is the order of confidence. A permission refusal has a name reserved
 * for it in the platform, so it is read from the name alone and nothing else can
 * take it. A duplicate is checked before an invalid descriptor, because a duplicate
 * message often carries the word "invalid" as well and the other way round does not
 * happen.
 *
 * The fallback is `Unsupported`, and it is a fallback rather than a guess: it says
 * this registration did not happen on this build and the reason was not one of the
 * three Jig can act on. `NotSupportedError` lands there too, which is the same
 * answer arrived at from the host's own word for it. `errorName` carries whatever
 * the host said either way, so nothing is lost by landing here.
 */
export const classifyRegistrationFailure = (detail: ThrownDetail): RegistrationCause => {
  if (detail.name === 'NotAllowedError') return 'NotAllowedError';
  if (DUPLICATE_ERROR_NAMES.includes(detail.name)) return 'DuplicateName';
  if (DUPLICATE_PHRASES.test(detail.message)) return 'DuplicateName';
  if (INVALID_ERROR_NAMES.includes(detail.name)) return 'InvalidDescriptor';
  if (INVALID_PHRASES.test(detail.message)) return 'InvalidDescriptor';
  return 'Unsupported';
};

/** The failure a rejection becomes. */
export const registrationRefused = (name: string, detail: ThrownDetail): RegistrationOutcome => ({
  ok: false,
  name,
  cause: classifyRegistrationFailure(detail),
  errorName: detail.name,
  message:
    detail.message === ''
      ? `Registration of ${name} was refused with ${detail.name}.`
      : detail.message,
});

/** The failure where there was nothing to call in the first place. */
export const registrationUnsupported = (name: string, message: string): RegistrationOutcome => ({
  ok: false,
  name,
  cause: 'Unsupported',
  errorName: 'Unsupported',
  message,
});

/** One line naming the tool and its cause, for a panel that wants the sentence made. */
export const describeOutcome = (outcome: RegistrationOutcome): string =>
  outcome.ok
    ? `${outcome.name} is registered.`
    : `${outcome.name} is not registered. ${outcome.cause}: ${outcome.message}`;
